import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import AppFooter from '../components/AppFooter'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import axios from 'axios'
import { Button } from '../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog'
import DataTable, { Column } from '../components/admin/DataTable'
import { formatMoney, getApiUrl } from '../lib/utils'
import {
  Users,
  Calendar,
  MessageSquare,
  Wallet,
  Receipt,
  Loader2,
  KeyRound,
  Copy,
  Check,
  Lock,
  ShieldAlert,
  RotateCcw,
} from 'lucide-react'
import { toast } from 'sonner'

const API = getApiUrl()

// Pure, render-stable helpers (module scope so they don't churn column memos).
const badge = 'px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap'
const dateMs = (v) => {
  if (!v) return null
  const t = Date.parse(v)
  return Number.isFinite(t) ? t : null
}
const dateText = (v) => (v ? new Date(v).toLocaleDateString() : '—')

const AdminPanel = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [events, setEvents] = useState([])
  const [rsvps, setRsvps] = useState([])
  const [transactions, setTransactions] = useState([])
  const [resets, setResets] = useState([])
  const [activeTab, setActiveTab] = useState('overview')

  // Password-reset flow: the user awaiting a confirm, the loading flag, the
  // one-time result to reveal, and whether the revealed value was copied.
  const [resetTarget, setResetTarget] = useState<any>(null)
  const [resetting, setResetting] = useState(false)
  const [resetResult, setResetResult] = useState<any>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required')
      navigate('/dashboard')
      return
    }
    fetchAdminData()
  }, [user, navigate])

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, eventsRes, rsvpsRes, txRes, resetsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/events`, { withCredentials: true }),
        axios.get(`${API}/admin/rsvps`, { withCredentials: true }),
        axios.get(`${API}/admin/transactions`, { withCredentials: true }),
        axios.get(`${API}/admin/password-reset-requests`, { withCredentials: true }),
      ])
      setStats(statsRes.data)
      setUsers(usersRes.data)
      setEvents(eventsRes.data)
      setRsvps(rsvpsRes.data)
      setTransactions(txRes.data)
      setResets(resetsRes.data)
    } catch (error) {
      toast.error('Failed to load admin data')
    } finally {
      setLoading(false)
    }
  }

  const pendingResets = useMemo(
    () => resets.filter((r) => r.status === 'pending'),
    [resets],
  )

  // Confirm + perform a reset. `target` may be a users-table row or a
  // reset-request row — both carry user_id / email / name.
  const confirmReset = useCallback(async () => {
    if (!resetTarget?.user_id) return
    setResetting(true)
    try {
      const res = await axios.post(
        `${API}/admin/users/${resetTarget.user_id}/reset-password`,
        {},
        { withCredentials: true },
      )
      setResetResult(res.data)
      setResetTarget(null)
      setCopied(false)
      // Refresh users (password status) and the reset queue (auto-resolved).
      const [usersRes, resetsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/password-reset-requests`, { withCredentials: true }),
      ])
      setUsers(usersRes.data)
      setResets(resetsRes.data)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset password')
    } finally {
      setResetting(false)
    }
  }, [resetTarget])

  const dismissRequest = useCallback(async (requestId: string) => {
    try {
      await axios.post(
        `${API}/admin/password-reset-requests/${requestId}/dismiss`,
        {},
        { withCredentials: true },
      )
      setResets((prev) =>
        prev.map((r) =>
          r.request_id === requestId ? { ...r, status: 'dismissed' } : r,
        ),
      )
    } catch (error) {
      toast.error('Could not dismiss request')
    }
  }, [])

  const copyTempPassword = useCallback(async () => {
    if (!resetResult?.temporary_password) return
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Copy failed — select the password and copy manually')
    }
  }, [resetResult])

  const currency = stats?.currency || 'GEL'
  const money = useCallback(
    (v) => (v === null || v === undefined ? '—' : `${formatMoney(v)} ${currency}`),
    [currency],
  )
  const statusClass = useCallback(
    (s) =>
      s === 'paid'
        ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
        : s === 'pending'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
          : 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
    [],
  )

  // ---- Column definitions per table -------------------------------------
  const userColumns: Column<any>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        accessor: (u) => u.name,
        widthClass: 'min-w-[200px]',
        cell: (u) => (
          <div className="flex items-center gap-3">
            {u.picture ? (
              <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-[#1b1033]/[0.06] dark:bg-white/10 rounded-full flex items-center justify-center">
                <span className="text-[#1b1033] dark:text-[#f4f0ff] text-sm font-medium">
                  {u.name?.[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <span className="font-medium text-[#1b1033] dark:text-[#f4f0ff]">
              {u.name}
            </span>
          </div>
        ),
      },
      { key: 'email', header: 'Email', accessor: (u) => u.email },
      {
        key: 'role',
        header: 'Role',
        accessor: (u) => u.role,
        filter: 'facet',
        cell: (u) => (
          <span
            className={`${badge} ${
              u.role === 'admin'
                ? 'bg-[#ff4d8d]/10 text-[#ff4d8d]'
                : 'bg-[#1b1033]/[0.06] dark:bg-white/10 text-[#1b1033] dark:text-[#f4f0ff]'
            }`}
          >
            {u.role}
          </span>
        ),
      },
      {
        // Passwords are bcrypt-hashed and can't be shown — this reflects only
        // whether a password is set (vs. a Google-only account). Reset via the
        // Actions column.
        key: 'password',
        header: 'Password',
        accessor: (u) => (u.has_password ? 'set' : 'none'),
        filter: 'facet',
        facetLabel: (v) => (v === 'set' ? 'Password set' : 'Google / none'),
        searchable: false,
        cell: (u) =>
          u.has_password ? (
            <span
              className={`${badge} inline-flex items-center gap-1 bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300`}
            >
              <Lock className="h-3 w-3" /> Set
            </span>
          ) : (
            <span
              className={`${badge} inline-flex items-center gap-1 bg-[#1b1033]/[0.06] text-[#4a3d66] dark:bg-white/10 dark:text-gray-400`}
            >
              Google / none
            </span>
          ),
      },
      {
        key: 'referrals',
        header: 'Referrals',
        accessor: (u) => u.referrals || 0,
        cell: (u) => (
          <span className="tabular-nums text-[#1b1033] dark:text-[#f4f0ff]">
            {u.referrals || 0}
          </span>
        ),
      },
      {
        key: 'balance',
        header: 'Balance',
        accessor: (u) => (u.account_balances || []).map((b) => b.amount).join(' '),
        cell: (u) =>
          (u.account_balances || []).length === 0 ? (
            <span className="text-[#4a3d66] dark:text-gray-400">—</span>
          ) : (
            <span className="tabular-nums text-[#1b1033] dark:text-[#f4f0ff]">
              {(u.account_balances || [])
                .map((b) => `${b.amount.toFixed(2)} ${b.currency}`)
                .join(' · ')}
            </span>
          ),
      },
      {
        key: 'events',
        header: 'Events',
        accessor: (u) => u.events_count ?? 0,
        align: 'right',
        filter: 'number',
      },
      {
        key: 'guests',
        header: 'Guests',
        accessor: (u) => u.guests_count ?? 0,
        align: 'right',
        filter: 'number',
      },
      {
        key: 'revenue',
        header: 'Revenue',
        accessor: (u) => Number(u.revenue ?? 0),
        align: 'right',
        filter: 'number',
        searchable: false,
        cell: (u) => (
          <span className="font-medium text-[#1b1033] dark:text-[#f4f0ff] whitespace-nowrap">
            {money(u.revenue)}
          </span>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        accessor: (u) => dateMs(u.created_at),
        csvAccessor: (u) => u.created_at || '',
        filter: 'date',
        searchable: false,
        cell: (u) => (
          <span className="text-[#4a3d66] dark:text-gray-400 text-sm tabular-nums">
            {dateText(u.created_at)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        accessor: () => '',
        sortable: false,
        searchable: false,
        align: 'right',
        widthClass: 'min-w-[120px]',
        cell: (u) => (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setResetTarget(u)}
              className="h-8 rounded-full border-[#1b1033]/12 bg-white px-3 text-xs font-semibold text-[#1b1033] hover:border-[#ff4d8d]/40 hover:text-[#ff4d8d] dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
            >
              <KeyRound className="mr-1.5 h-3.5 w-3.5" />
              Reset
            </Button>
          </div>
        ),
      },
    ],
    [money],
  )

  const eventColumns: Column<any>[] = useMemo(
    () => [
      {
        key: 'title',
        header: 'Event',
        accessor: (e) => e.title,
        widthClass: 'min-w-[180px]',
        cell: (e) => (
          <span className="font-medium text-[#1b1033] dark:text-[#f4f0ff]">
            {e.title}
          </span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        accessor: (e) => e.event_type,
        filter: 'facet',
        cell: (e) => (
          <span className="text-[#4a3d66] dark:text-gray-400 capitalize">
            {e.event_type}
          </span>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        accessor: (e) => dateMs(e.date),
        csvAccessor: (e) => e.date || '',
        filter: 'date',
        searchable: false,
        cell: (e) => (
          <span className="text-[#4a3d66] dark:text-gray-400 tabular-nums">
            {e.date || '—'}
          </span>
        ),
      },
      { key: 'location', header: 'Location', accessor: (e) => e.location },
    ],
    [],
  )

  const rsvpColumns: Column<any>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Guest',
        accessor: (r) => r.name,
        widthClass: 'min-w-[160px]',
        cell: (r) => (
          <span className="font-medium text-[#1b1033] dark:text-[#f4f0ff]">
            {r.name}
          </span>
        ),
      },
      { key: 'contact', header: 'Contact', accessor: (r) => r.email || r.phone },
      {
        key: 'status',
        header: 'Status',
        accessor: (r) => r.rsvp_status,
        filter: 'facet',
        cell: (r) => (
          <span
            className={`${badge} ${
              r.rsvp_status === 'attending'
                ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
                : r.rsvp_status === 'declined'
                  ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
                  : r.rsvp_status === 'maybe'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
            }`}
          >
            {r.rsvp_status}
          </span>
        ),
      },
    ],
    [],
  )

  const txColumns: Column<any>[] = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        accessor: (t) => dateMs(t.created_at),
        filter: 'date',
        searchable: false,
        widthClass: 'min-w-[120px]',
        cell: (t) => (
          <span className="text-[#4a3d66] dark:text-gray-400 text-sm tabular-nums whitespace-nowrap">
            {dateText(t.created_at)}
          </span>
        ),
      },
      {
        key: 'user',
        header: 'User',
        accessor: (t) => t.user_email,
        cell: (t) => (
          <span className="text-[#1b1033] dark:text-[#f4f0ff]">
            {t.user_email || '—'}
          </span>
        ),
      },
      {
        key: 'purpose',
        header: 'Purpose',
        accessor: (t) => t.purpose,
        filter: 'facet',
        facetLabel: (v) => v.replace(/_/g, ' '),
        cell: (t) => (
          <span className="text-[#4a3d66] dark:text-gray-400 capitalize">
            {(t.purpose || '').replace(/_/g, ' ') || '—'}
          </span>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        accessor: (t) => Number(t.amount ?? 0),
        align: 'right',
        filter: 'number',
        searchable: false,
        cell: (t) => (
          <span className="font-medium text-[#1b1033] dark:text-[#f4f0ff] whitespace-nowrap">
            {formatMoney(t.amount)} {t.currency}
          </span>
        ),
      },
      {
        key: 'method',
        header: 'Method',
        accessor: (t) => t.method || t.provider,
        filter: 'facet',
      },
      {
        key: 'status',
        header: 'Status',
        accessor: (t) => t.status,
        filter: 'facet',
        cell: (t) => (
          <span className={`${badge} ${statusClass(t.status)}`}>{t.status}</span>
        ),
      },
    ],
    [statusClass],
  )

  const resetColumns: Column<any>[] = useMemo(
    () => [
      {
        key: 'user',
        header: 'User',
        accessor: (r) => r.name || r.email,
        widthClass: 'min-w-[200px]',
        cell: (r) => (
          <div className="min-w-0">
            <p className="font-medium text-[#1b1033] dark:text-[#f4f0ff] truncate">
              {r.name || '—'}
            </p>
            <p className="text-xs text-[#4a3d66] dark:text-gray-400 truncate">{r.email}</p>
          </div>
        ),
      },
      {
        key: 'requested',
        header: 'Requested',
        accessor: (r) => dateMs(r.created_at),
        csvAccessor: (r) => r.created_at || '',
        filter: 'date',
        searchable: false,
        widthClass: 'min-w-[150px]',
        cell: (r) => (
          <span className="whitespace-nowrap text-sm tabular-nums text-[#4a3d66] dark:text-gray-400">
            {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
          </span>
        ),
      },
      {
        key: 'note',
        header: 'Note',
        accessor: (r) => r.note || '',
        widthClass: 'min-w-[160px]',
        cell: (r) =>
          r.note ? (
            <span className="text-sm text-[#4a3d66] dark:text-gray-400">{r.note}</span>
          ) : (
            <span className="text-[#4a3d66]/50 dark:text-gray-600">—</span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        accessor: (r) => r.status,
        filter: 'facet',
        cell: (r) => (
          <span
            className={`${badge} capitalize ${
              r.status === 'pending'
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                : r.status === 'resolved'
                  ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
                  : 'bg-[#1b1033]/[0.06] text-[#4a3d66] dark:bg-white/10 dark:text-gray-400'
            }`}
          >
            {r.status}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        accessor: () => '',
        sortable: false,
        searchable: false,
        align: 'right',
        widthClass: 'min-w-[180px]',
        cell: (r) =>
          r.status === 'pending' ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                disabled={!r.user_id}
                onClick={() => setResetTarget(r)}
                className="h-8 rounded-full border-[#1b1033]/12 bg-white px-3 text-xs font-semibold text-[#1b1033] hover:border-[#ff4d8d]/40 hover:text-[#ff4d8d] disabled:opacity-40 dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
              >
                <KeyRound className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
              <Button
                variant="ghost"
                onClick={() => dismissRequest(r.request_id)}
                className="h-8 rounded-full px-3 text-xs font-medium text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400"
              >
                Dismiss
              </Button>
            </div>
          ) : (
            <span className="text-xs text-[#4a3d66]/60 dark:text-gray-500">—</span>
          ),
      },
    ],
    [dismissRequest],
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-celebrate flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d8d]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-[#1e1438] border-b border-[#1b1033]/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <BrandLogo className="mr-3" />
            <DashboardButton />
            <div className="ml-auto flex items-center gap-2 sm:gap-4">
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100dvh-4rem)] max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold font-display leading-none tracking-tight text-[#1b1033] dark:text-[#f4f0ff]">
            Admin Panel
          </h1>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <div className="tile p-5 sm:p-6 min-w-0">
              <div className="flex flex-col gap-4">
                <div className="w-10 h-10 bg-[#ff4d8d]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#ff4d8d]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-3xl sm:text-4xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display leading-none tabular-nums">
                    {stats.total_users}
                  </p>
                  <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-gray-400 leading-tight">
                    Total Users
                  </p>
                </div>
              </div>
            </div>

            <div className="tile p-5 sm:p-6 min-w-0">
              <div className="flex flex-col gap-4">
                <div className="w-10 h-10 bg-[#1b1033]/[0.06] dark:bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#1b1033] dark:text-[#f4f0ff]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-3xl sm:text-4xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display leading-none tabular-nums">
                    {stats.total_events}
                  </p>
                  <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-gray-400 leading-tight">
                    Total Events
                  </p>
                </div>
              </div>
            </div>

            <div className="tile p-5 sm:p-6 min-w-0">
              <div className="flex flex-col gap-4">
                <div className="w-10 h-10 bg-[#1b1033]/[0.06] dark:bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-[#1b1033] dark:text-[#f4f0ff]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-3xl sm:text-4xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display leading-none tabular-nums">
                    {stats.total_guests}
                  </p>
                  <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-gray-400 leading-tight">
                    Total RSVPs
                  </p>
                </div>
              </div>
            </div>

            <div className="tile p-5 sm:p-6 min-w-0">
              <div className="flex flex-col gap-4">
                <div className="w-10 h-10 bg-[#ff4d8d]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-[#ff4d8d]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-2xl sm:text-3xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display leading-none tabular-nums">
                    {money(stats.storyfy_revenue)}
                  </p>
                  <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-gray-400 leading-tight">
                    Revenue
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full justify-start overflow-x-auto scrollbar-hide bg-white dark:bg-[#1e1438] border border-[#1b1033]/10 dark:border-white/10 rounded-xl p-1 mb-6">
            <TabsTrigger
              value="overview"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger
              value="events"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger
              value="rsvps"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              RSVPs ({rsvps.length})
            </TabsTrigger>
            <TabsTrigger
              value="finance"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              Finance
            </TabsTrigger>
            <TabsTrigger
              value="resets"
              className="rounded-lg shrink-0 px-3 sm:px-5 text-[#4a3d66] dark:text-gray-400 data-[state=active]:bg-[#1b1033] dark:data-[state=active]:bg-white/10 data-[state=active]:text-white dark:data-[state=active]:text-[#f4f0ff] data-[state=active]:shadow-none"
            >
              Password resets
              {pendingResets.length > 0 && (
                <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#ff4d8d] px-1.5 text-[11px] font-bold text-white tabular-nums">
                  {pendingResets.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <DataTable
              data={users}
              columns={userColumns}
              getRowId={(u) => u.user_id}
              searchPlaceholder="Search users by name or email…"
              initialSort={{ key: 'created', dir: 'desc' }}
              emptyMessage="No users yet"
              csvFilename="storyfy-users"
            />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <DataTable
              data={events}
              columns={eventColumns}
              getRowId={(e) => e.event_id}
              searchPlaceholder="Search events by title, type or location…"
              initialSort={{ key: 'date', dir: 'desc' }}
              emptyMessage="No events yet"
              csvFilename="storyfy-events"
            />
          </TabsContent>

          {/* RSVPs Tab */}
          <TabsContent value="rsvps">
            <DataTable
              data={rsvps}
              columns={rsvpColumns}
              getRowId={(r) => r.guest_id}
              searchPlaceholder="Search guests by name or contact…"
              emptyMessage="No RSVPs yet"
              csvFilename="storyfy-rsvps"
            />
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance">
            {/* Revenue breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
              {[
                { label: 'Storyfy Revenue', value: stats?.storyfy_revenue, accent: true },
                { label: 'Add-on Sales', value: stats?.addon_revenue },
                { label: 'AI Credit Sales', value: stats?.ai_revenue },
                { label: 'Gift Commission', value: stats?.commission_revenue },
              ].map((c) => (
                <div key={c.label} className="tile p-5 min-w-0">
                  <p
                    className={`text-xl sm:text-2xl font-semibold font-display leading-none tabular-nums ${
                      c.accent ? 'text-[#ff4d8d]' : 'text-[#1b1033] dark:text-[#f4f0ff]'
                    }`}
                  >
                    {money(c.value)}
                  </p>
                  <p className="mt-1.5 text-sm text-[#4a3d66] dark:text-gray-400">{c.label}</p>
                </div>
              ))}
            </div>

            {/* Secondary figures */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 text-sm text-[#4a3d66] dark:text-gray-400">
              <span>
                Gift volume processed:{' '}
                <strong className="text-[#1b1033] dark:text-[#f4f0ff]">{money(stats?.gift_volume)}</strong>
              </span>
              <span>
                Paid: <strong className="text-[#1b1033] dark:text-[#f4f0ff]">{stats?.paid_payments ?? 0}</strong>
              </span>
              <span>
                Pending: <strong className="text-[#1b1033] dark:text-[#f4f0ff]">{stats?.pending_payments ?? 0}</strong>
              </span>
              <span>
                Referral payouts:{' '}
                <strong className="text-[#1b1033] dark:text-[#f4f0ff]">{stats?.referral_paid ?? 0}</strong>
              </span>
              <span>
                AI generations:{' '}
                <strong className="text-[#1b1033] dark:text-[#f4f0ff]">{stats?.ai_generations ?? 0}</strong>
              </span>
            </div>

            {/* Transactions */}
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4 text-[#4a3d66]" />
              <h3 className="text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                Transactions
              </h3>
            </div>
            <DataTable
              data={transactions}
              columns={txColumns}
              getRowId={(t) => t.payment_id}
              searchPlaceholder="Search transactions by user…"
              initialSort={{ key: 'date', dir: 'desc' }}
              emptyMessage="No transactions yet"
              csvFilename="storyfy-transactions"
            />
          </TabsContent>

          {/* Password Resets Tab */}
          <TabsContent value="resets">
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-[#1b1033]/10 bg-white/60 p-4 text-sm text-[#4a3d66] dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400">
              <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#ff4d8d]" />
              <p>
                Requests from the login page's “Forgot password” form. Reset the
                account to a temporary password, then share it with the person
                directly. Passwords are encrypted and can never be shown — only
                reset. Resetting also signs the user out of all active sessions.
              </p>
            </div>
            <DataTable
              data={resets}
              columns={resetColumns}
              getRowId={(r) => r.request_id}
              searchPlaceholder="Search reset requests by name or email…"
              initialSort={{ key: 'requested', dir: 'desc' }}
              emptyMessage="No password-reset requests"
              csvFilename="storyfy-password-resets"
            />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="tile p-6">
                <h3 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display mb-4">
                  Recent Users
                </h3>
                <div className="space-y-3">
                  {users.slice(0, 5).map((user) => (
                    <div key={user.user_id} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#1b1033]/[0.06] dark:bg-white/10 rounded-full flex items-center justify-center">
                        <span className="text-[#1b1033] dark:text-[#f4f0ff] text-sm font-medium">
                          {user.name?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                          {user.name}
                        </p>
                        <p className="text-xs text-[#4a3d66] dark:text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tile p-6">
                <h3 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display mb-4">
                  Recent Events
                </h3>
                <div className="space-y-3">
                  {events.slice(0, 5).map((event) => (
                    <div
                      key={event.event_id}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                          {event.title}
                        </p>
                        <p className="text-xs text-[#4a3d66] dark:text-gray-400">
                          {event.event_type}
                        </p>
                      </div>
                      <span className="text-xs text-[#4a3d66] dark:text-gray-400 tabular-nums">
                        {event.date}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirm reset */}
      <Dialog
        open={!!resetTarget}
        onOpenChange={(o) => {
          if (!o && !resetting) setResetTarget(null)
        }}
      >
        <DialogContent className="rounded-bubble border-[#1b1033]/10 bg-white dark:border-white/10 dark:bg-[#1e1438]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1b1033] dark:text-[#f4f0ff]">
              <KeyRound className="h-5 w-5 text-[#ff4d8d]" />
              Reset password
            </DialogTitle>
            <DialogDescription className="text-[#4a3d66] dark:text-gray-400">
              A fresh temporary password will be generated for{' '}
              <span className="font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                {resetTarget?.name || resetTarget?.email}
              </span>
              {resetTarget?.name && resetTarget?.email ? ` (${resetTarget.email})` : ''}. You'll
              see it once, to share with them directly.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-[#1b1033]/[0.04] p-3 text-sm text-[#4a3d66] dark:bg-white/[0.04] dark:text-gray-400">
            This immediately replaces their current password and signs them out
            of every active session.
            {resetTarget && resetTarget.has_password === false && (
              <span className="mt-1.5 block font-medium text-[#b45309] dark:text-amber-300">
                This account signs in with Google — a reset adds a password login
                alongside it.
              </span>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetTarget(null)}
              disabled={resetting}
              className="rounded-full border-[#1b1033]/12 bg-white text-[#1b1033] dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmReset}
              disabled={resetting}
              className="rounded-full btn-gradient font-semibold text-white"
            >
              {resetting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-1.5">
                  <RotateCcw className="h-4 w-4" />
                  Reset password
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal the generated password once */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="rounded-bubble border-[#1b1033]/10 bg-white dark:border-white/10 dark:bg-[#1e1438]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1b1033] dark:text-[#f4f0ff]">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              Temporary password ready
            </DialogTitle>
            <DialogDescription className="text-[#4a3d66] dark:text-gray-400">
              Share this with{' '}
              <span className="font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                {resetResult?.name || resetResult?.email}
              </span>
              . For security it{"'"}s shown only once — copy it now.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-[#1b1033]/12 bg-[#1b1033]/[0.03] p-2 pl-4 dark:border-white/10 dark:bg-white/[0.04]">
            <code className="flex-1 select-all font-mono text-lg font-semibold tracking-wide text-[#1b1033] dark:text-[#f4f0ff]">
              {resetResult?.temporary_password}
            </code>
            <Button
              onClick={copyTempPassword}
              className="rounded-lg btn-gradient px-3 font-semibold text-white"
            >
              {copied ? (
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4" /> Copied
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Copy className="h-4 w-4" /> Copy
                </span>
              )}
            </Button>
          </div>
          {resetResult?.email && (
            <p className="text-xs text-[#4a3d66] dark:text-gray-400">
              Account: {resetResult.email}. They can sign in with this password
              right away.
            </p>
          )}
          <DialogFooter>
            <Button
              onClick={() => setResetResult(null)}
              className="rounded-full btn-gradient font-semibold text-white"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppFooter />
    </div>
  )
}

export default AdminPanel
